/**
 * BoardsTab — top-level entry point: 2-col layout (board list | board view).
 */

import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  Squares2X2Icon,
  TrashIcon,
  PencilIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  LockClosedIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';
import { useAuth } from '@/core/auth';
import { useBoards, useCreateBoard, useDeleteBoard } from './api';
import type { BoardVisibility } from './types';
import { BoardView } from './BoardView';
import './boards.css';

const NewBoardForm: React.FC<{ onCreated: (id: string) => void; onClose: () => void }> = ({ onCreated, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<BoardVisibility>('shared');
  const createBoard = useCreateBoard();
  const submit = async () => {
    if (!name.trim()) return;
    const board = await createBoard.mutateAsync({ name: name.trim(), description: description.trim() || undefined, visibility });
    onCreated(board.id);
    onClose();
  };
  return (
    <div className="bd-modal-backdrop" onClick={onClose}>
      <div className="bd-modal" onClick={e => e.stopPropagation()}>
        <div className="bd-modal-header">
          <h3>Nouveau board</h3>
        </div>
        <div className="bd-modal-body">
          <label className="bd-modal-label">Nom</label>
          <input
            className="bd-modal-input"
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          />
          <label className="bd-modal-label">Description (optionnel)</label>
          <textarea
            className="bd-modal-input"
            rows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          <label className="bd-modal-label">Visibilité</label>
          <div className="bd-vis-choice">
            <button
              type="button"
              className={`bd-vis-opt ${visibility === 'shared' ? 'bd-vis-opt--on' : ''}`}
              onClick={() => setVisibility('shared')}
            >
              <UsersIcon className="w-4 h-4" />
              <span><strong>Partagé</strong><small>Visible par toute l'équipe</small></span>
            </button>
            <button
              type="button"
              className={`bd-vis-opt ${visibility === 'private' ? 'bd-vis-opt--on' : ''}`}
              onClick={() => setVisibility('private')}
            >
              <LockClosedIcon className="w-4 h-4" />
              <span><strong>Privé</strong><small>Visible par vous seul</small></span>
            </button>
          </div>
        </div>
        <div className="bd-modal-footer">
          <button className="bd-btn-ghost" onClick={onClose}>Annuler</button>
          <button className="bd-btn-primary" onClick={submit} disabled={!name.trim() || createBoard.isPending}>
            Créer
          </button>
        </div>
      </div>
    </div>
  );
};

export const BoardsTab: React.FC = () => {
  const { data: boards, isLoading } = useBoards();
  const deleteBoard = useDeleteBoard();
  const qc = useQueryClient();
  const { user } = useAuth();
  const currentUserId = user?.id ? String(user.id) : null;
  // Only the owner can change a board's visibility. Legacy boards with no
  // owner stay editable by any admin/bureau user.
  const canManage = (ownerId: string | null) => !ownerId || String(ownerId) === currentUserId;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  // Sidebar collapsed by default; user preference survives reloads.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('bd-sidebar-collapsed') !== '0'; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('bd-sidebar-collapsed', collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  const renameBoard = async (id: string, currentName: string) => {
    const nv = prompt('Renommer le board', currentName);
    if (!nv || !nv.trim() || nv.trim() === currentName) return;
    try {
      await apiClient.put(`/api/team-boards/${id}`, { name: nv.trim() });
      qc.invalidateQueries({ queryKey: ['team-boards'] });
    } catch (e) {
      alert('Échec du renommage');
    }
  };

  const toggleVisibility = async (id: string, current: BoardVisibility) => {
    const next: BoardVisibility = current === 'shared' ? 'private' : 'shared';
    try {
      await apiClient.put(`/api/team-boards/${id}`, { visibility: next });
      qc.invalidateQueries({ queryKey: ['team-boards'] });
      qc.invalidateQueries({ queryKey: ['team-boards', id] });
    } catch (e) {
      alert('Échec du changement de visibilité');
    }
  };

  // Deep-link from a notification: ?board=<id>&row=<id> selects the board and
  // opens the row.
  const [searchParams] = useSearchParams();
  const boardParam = searchParams.get('board');
  const rowParam = searchParams.get('row');
  useEffect(() => {
    if (boardParam) setSelectedId(boardParam);
  }, [boardParam, rowParam]);

  // Auto-select first board when list loads (unless a deep-link board is set).
  useEffect(() => {
    if (!selectedId && !boardParam && boards && boards.length > 0) {
      setSelectedId(boards[0].id);
    }
  }, [boards, selectedId, boardParam]);

  return (
    <div className={`bd-layout ${collapsed ? 'bd-layout--collapsed' : ''}`}>
      {showNew && (
        <NewBoardForm
          onCreated={id => setSelectedId(id)}
          onClose={() => setShowNew(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`bd-sidebar ${collapsed ? 'bd-sidebar--collapsed' : ''}`}>
        <div className="bd-sidebar-header">
          {!collapsed && (
            <h2 className="bd-sidebar-title">
              <Squares2X2Icon className="w-5 h-5" /> Boards
            </h2>
          )}
          <div className="bd-sidebar-header-actions">
            {!collapsed && (
              <button
                className="bd-btn-icon-primary"
                onClick={() => setShowNew(true)}
                title="Nouveau board"
              >
                <PlusIcon className="w-4 h-4" />
              </button>
            )}
            <button
              className="bd-icon-btn"
              onClick={() => setCollapsed(c => !c)}
              title={collapsed ? 'Déplier la liste des boards' : 'Replier la liste des boards'}
            >
              {collapsed
                ? <ChevronDoubleRightIcon className="w-4 h-4" />
                : <ChevronDoubleLeftIcon className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {!collapsed ? (
          <div className="bd-sidebar-list">
            {isLoading && <div className="bd-sidebar-loading">Chargement…</div>}
            {!isLoading && (boards || []).length === 0 && (
              <div className="bd-sidebar-empty">
                Aucun board.
                <button className="bd-btn-link" onClick={() => setShowNew(true)}>Créer le premier</button>
              </div>
            )}
            {([
              { key: 'shared', label: 'Partagés', Icon: UsersIcon },
              { key: 'private', label: 'Privés', Icon: LockClosedIcon },
            ] as const).map(section => {
              const items = (boards || []).filter(b => (b.visibility || 'shared') === section.key);
              if (items.length === 0) return null;
              const SIcon = section.Icon;
              return (
                <div className="bd-sidebar-group" key={section.key}>
                  <div className="bd-sidebar-group-hd"><SIcon className="w-3.5 h-3.5" /> {section.label} <span>{items.length}</span></div>
                  {items.map(b => (
                    <div
                      key={b.id}
                      className={`bd-sidebar-item ${selectedId === b.id ? 'bd-sidebar-item--active' : ''}`}
                      onClick={() => setSelectedId(b.id)}
                    >
                      <div className="bd-sidebar-item-main">
                        <span className="bd-sidebar-item-name">{b.name}</span>
                        <span className="bd-sidebar-item-count">{b.row_count} ligne{b.row_count !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="bd-sidebar-item-actions">
                        {canManage(b.owner_id) ? (
                          <button
                            className="bd-icon-btn"
                            onClick={e => { e.stopPropagation(); toggleVisibility(b.id, b.visibility || 'shared'); }}
                            title={b.visibility === 'private' ? 'Rendre partagé' : 'Rendre privé'}
                          >
                            {b.visibility === 'private'
                              ? <LockClosedIcon className="w-3.5 h-3.5" />
                              : <UsersIcon className="w-3.5 h-3.5" />}
                          </button>
                        ) : (
                          <span
                            className="bd-icon-btn bd-icon-btn--readonly"
                            title={b.visibility === 'private' ? 'Privé (créé par quelqu’un d’autre)' : 'Partagé (créé par quelqu’un d’autre)'}
                          >
                            {b.visibility === 'private'
                              ? <LockClosedIcon className="w-3.5 h-3.5" />
                              : <UsersIcon className="w-3.5 h-3.5" />}
                          </span>
                        )}
                        <button
                          className="bd-icon-btn"
                          onClick={e => { e.stopPropagation(); renameBoard(b.id, b.name); }}
                          title="Renommer"
                        >
                          <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          className="bd-icon-btn"
                          onClick={e => {
                            e.stopPropagation();
                            if (confirm(`Supprimer le board "${b.name}" et toutes ses données ?`)) {
                              deleteBoard.mutate(b.id, {
                                onSuccess: () => { if (selectedId === b.id) setSelectedId(null); }
                              });
                            }
                          }}
                          title="Supprimer"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bd-sidebar-rail">
            {(['shared', 'private'] as const).map((vis, idx) => {
              const items = (boards || []).filter(b => (b.visibility || 'shared') === vis);
              if (items.length === 0) return null;
              return (
                <React.Fragment key={vis}>
                  {idx === 1 && <span className="bd-rail-divider" title="Privés" />}
                  {items.map(b => {
                    const initials = b.name
                      .split(/\s+/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map(w => w.charAt(0))
                      .join('')
                      .toUpperCase() || '?';
                    return (
                      <button
                        key={b.id}
                        className={`bd-rail-item ${selectedId === b.id ? 'bd-rail-item--active' : ''} ${vis === 'private' ? 'bd-rail-item--private' : ''}`}
                        onClick={() => setSelectedId(b.id)}
                        title={`${b.name}${vis === 'private' ? ' · privé' : ' · partagé'}`}
                      >
                        {initials}
                        {vis === 'private' && (
                          <span className="bd-rail-lock"><LockClosedIcon className="w-2.5 h-2.5" /></span>
                        )}
                      </button>
                    );
                  })}
                </React.Fragment>
              );
            })}
            <button
              className="bd-rail-item bd-rail-item--add"
              onClick={() => setShowNew(true)}
              title="Nouveau board"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </aside>

      {/* Main */}
      <main className="bd-main">
        {selectedId ? (
          <BoardView boardId={selectedId} openRowId={selectedId === boardParam ? rowParam : null} />
        ) : (
          <div className="bd-placeholder">
            <Squares2X2Icon className="w-16 h-16" />
            <h3>Aucun board sélectionné</h3>
            <p>Choisis un board à gauche ou crée-en un nouveau.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default BoardsTab;
